// Demo session — backs up the user's real localStorage state, seeds demo
// data in its place, and restores on exit.
//
// Backup is held in localStorage so both tabs (school admin + teacher) see
// the same demo data and exiting demo from either tab propagates to the
// other via the storage event in store.ts. The per-tab role is in
// sessionStorage.
//
// School role lands the user in the onboarding wizard (pre-populated with
// sensible defaults including the dummy teacher) so they walk through the
// full setup experience. Teacher role skips the wizard and renders CMCApp
// directly via DemoApp.

import { activateDemo, deactivateDemo } from './store';
import {
  buildSeedSchool,
  buildSeedLesson,
  buildSeedCoverLessons,
  buildSeedFeedback,
  buildSeedClasses,
  buildSeedPillarSuggestions,
  buildSeedPupils,
  DEMO_EMAILS,
  DEMO_SEED_VERSION,
} from './config';
import type { OnboardingState } from '@/lib/onboarding-store';
import type { AppState, ClassEntity } from '@/lib/app-store';
import { countArchivedEntries } from '@/lib/app-store';
import { isDemoMode, pushInboxEmail } from '@/lib/demo/store';

const BACKUP_KEY = '__cmc_real_state_backup_v1';
const ROLE_KEY = 'cmc_demo_role';

// Real keys touched by the production app. On the first tab to enter
// demo, we backup their values; on exit we restore. Subsequent tabs that
// enter demo don't re-backup (the demo data would overwrite the real
// backup) — they just join the existing session.
const LS_KEYS = [
  'cmc_onboarding_state',
  'cmc_app_state',
  'cmc_tutorial_state',
  // Global, not user-suffixed. The demo clears it so the dashboard tour runs
  // again; without it in this list that clear was permanent, and a real admin
  // who dipped into the demo came back and replayed their whole school tour.
  'cmc_school_tutorial_state',
  'cmc_cover_tutorial_state',
  'cmc_invite_codes',
  'cmc_valid_invite_codes',
  'cmc_school_credentials',
  'cmc_teacher_credentials',
  'cmc_emergency_alerts',
  'cmc_cover_assignments',
  'cmc_remember_school',
];

/**
 * Per-user keys the demo must also clear, matched by prefix because each is
 * suffixed with the signed-in user's id.
 *
 * These are the "you have already been here" flags: which tours an account has
 * seen, which portals it has walked into, which tiles are still offering
 * something. They are exactly right for a real account and exactly wrong for a
 * demo, which is a showcase that has to open the same way every time. A second
 * run through the demo otherwise landed on a flat dashboard: nothing pulsing,
 * nothing dimmed, no order to the journey, because the first run had answered
 * all of it.
 *
 * Backed up and restored like every other key, so a real account that dips into
 * the demo gets its own history back on the way out.
 */
const LS_PREFIXES = [
  'cmc_classes_visited_',
  'cmc_my_teaching_visited_',
  'cmc_class_profiles_highlight_',
  'cmc_cover_portal_highlight_',
  'cmc_admin_portal_nudge_',
  'cmc_admin_teacher_tour_seen_',
  'cmc_classes_tutorial_seen_',
  'cmc_retention_tutorial_seen_',
];

/** Every stored key currently matching one of those prefixes. */
function prefixedKeys(): string[] {
  const out: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && LS_PREFIXES.some(p => k.startsWith(p))) out.push(k);
    }
  } catch { /* storage unavailable */ }
  return out;
}

const SS_KEYS = [
  'cmc_school_token',
  'cmc_teacher_token',
  'cmc_teacher_id',
  'cmc_cover_school_id',
  'cmc_first_login',
  // Written by the setup celebration, read once by the dashboard. Cleared on
  // exit so a flag that was never consumed cannot start a tour unasked later.
  'cmc_tour_consent',
  // Counters behind the capped sample policy assistant: the demo's own, and
  // the cover tour's, which runs inside the demo too.
  'cmc_demo_policy_chat_count',
  'cmc_tour_policy_chat_count',
];

interface Backup {
  ls: Record<string, string>;
  ss: Record<string, string>;
}

function readBackup(): Backup | null {
  try {
    const raw = localStorage.getItem(BACKUP_KEY);
    return raw ? (JSON.parse(raw) as Backup) : null;
  } catch {
    return null;
  }
}

function writeBackup(backup: Backup) {
  try {
    localStorage.setItem(BACKUP_KEY, JSON.stringify(backup));
  } catch {
    // ignore
  }
}

function clearBackup() {
  try {
    localStorage.removeItem(BACKUP_KEY);
  } catch {
    // ignore
  }
}

export function getDemoRole(): 'school' | 'teacher' | 'cover' | null {
  try {
    const r = sessionStorage.getItem(ROLE_KEY);
    if (r === 'school' || r === 'teacher' || r === 'cover') return r;
  } catch {
    // ignore
  }
  return null;
}

function backupRealState() {
  // If a backup already exists, another tab (or a previous demo session
  // that wasn't exited cleanly) created it. Don't overwrite — the existing
  // backup is the real state.
  if (readBackup()) return;
  const backup: Backup = { ls: {}, ss: {} };
  for (const k of [...LS_KEYS, ...prefixedKeys()]) {
    const v = localStorage.getItem(k);
    if (v !== null) {
      backup.ls[k] = v;
      localStorage.removeItem(k);
    }
  }
  for (const k of SS_KEYS) {
    const v = sessionStorage.getItem(k);
    if (v !== null) {
      backup.ss[k] = v;
      sessionStorage.removeItem(k);
    }
  }
  writeBackup(backup);
}

function clearAllDemoLikeState() {
  for (const k of [...LS_KEYS, ...prefixedKeys()]) localStorage.removeItem(k);
  for (const k of SS_KEYS) sessionStorage.removeItem(k);
}

// Build the shared demo state: full appState (school, lesson, feedback) +
// onboarding schoolData with the wizard mid-flight at step 1. This is
// written once by whichever tab enters demo first; subsequent tabs reuse
// the existing localStorage.
// Deliberately outside LS_KEYS and clearAllDemoLikeState: it records which
// seed a browser last received, and the whole point is that it SURVIVES a demo
// exit so the next entry can tell whether the content has moved on. Harmless
// residue on a real user's machine, and clearing it would defeat it.
const SEED_VERSION_KEY = 'cmc_demo_seed_version';

function seedSharedDemoData() {
  // The inbox goes with the content. Its emails carry deep links into seeded
  // lessons, and delivery is idempotent by kind, so a surviving email would
  // both point at the old data and block the new one from ever arriving.
  try {
    localStorage.removeItem('cmc_demo_inbox_v1');
    localStorage.setItem(SEED_VERSION_KEY, DEMO_SEED_VERSION);
  } catch { /* storage unavailable */ }
  const seedSchool = buildSeedSchool();
  const seedLesson = buildSeedLesson(seedSchool);
  const seedCoverLessons = buildSeedCoverLessons(seedSchool);
  const seedFeedback = buildSeedFeedback();

  // Onboarding state — pre-populated as "user is mid-wizard". When the
  // school admin tab mounts, the OnboardingWizard sees userType='school'
  // and currentStep=1 and starts at SchoolStep1Verify with all fields
  // already filled in.
  const onboarding: OnboardingState = {
    isComplete: false,
    userType: 'school',
    currentStep: 1,
    schoolData: {
      schoolName: seedSchool.name,
      urn: seedSchool.urn,
      dfeNumber: seedSchool.dfeNumber,
      address: seedSchool.address,
      postcode: seedSchool.postcode,
      phone: '02012345678',
      contactFirstName: 'Sarah',
      contactLastName: 'Patel',
      contactTitle: 'Mrs',
      contactEmail: DEMO_EMAILS.schoolAdmin,
      contactPhone: '02012345678',
      secondaryContacts: (seedSchool.contacts?.secondaryContacts ?? []).map((c, i) => ({
        id: `demo-secondary-${i + 1}`,
        name: c.name,
        email: c.email,
        role: c.role,
        phone: c.phone,
      })),
      staff: seedSchool.staff,
      policies: [
        {
          id: 'demo-policy-1',
          name: 'Behaviour Policy',
          type: 'behaviour',
          fileName: 'demo-behaviour-policy.pdf',
          uploadedAt: new Date().toISOString(),
        },
      ],
      periods: seedSchool.periods.map((p) => `Period ${p.number}`),
      subjects: seedSchool.subjects,
      departments: seedSchool.departments,
      rooms: seedSchool.rooms,
      yearGroups: seedSchool.yearGroups,
      schoolTypes: ['secondary'],
      periodConfig: {
        numberOfPeriods: seedSchool.periods.length,
        periods: seedSchool.periods.map((p) => ({
          name: `Period ${p.number}`,
          startTime: p.startTime,
          endTime: p.endTime,
        })),
        periodDuration: 60,
        schoolStartTime: '08:30',
        breakTime: { startTime: '10:30', endTime: '10:45' },
        lunchTime: { startTime: '12:45', endTime: '13:30' },
      },
      customYearGroups: [],
      logoUrl: '',
      primaryColor: seedSchool.primary,
      gdprConsent: true,
    },
    teacherCode: '',
    teacherName: '',
    teacherEmail: '',
    teacherDepartment: '',
    tutorialComplete: false,
    tutorialStep: 0,
    sendInvitesLater: false,
  };
  localStorage.setItem('cmc_onboarding_state', JSON.stringify(onboarding));

  // AppState — pre-seeded so the teacher tab has something to show even
  // before the school admin completes the wizard. When the wizard finishes
  // it rebuilds appState.school from onboarding (preserving lessons +
  // feedback) so any tweaks the user made flow through to both tabs.
  const appState: AppState = {
    school: seedSchool,
    lessons: [seedLesson, ...seedCoverLessons],
    feedback: [seedFeedback],
    currentUser: null,
  };
  localStorage.setItem('cmc_app_state', JSON.stringify(appState));

  // Phase 1: seed Class + Pupil entities so the Classes tab in the
  // demo school admin view has something to render immediately.
  // Un-seen, so the dashboard tour runs. It is what arms the portal nudges on
  // its way out, and those are what make My Teaching pulse.
  localStorage.removeItem('cmc_school_tutorial_state');
  localStorage.setItem('cmc_demo_classes_v1', JSON.stringify(buildSeedClasses()));
  // Same key the /pillar-suggestions mock reads.
  localStorage.setItem('cmc_demo_pillar_suggestions', JSON.stringify(buildSeedPillarSuggestions()));
  localStorage.setItem('cmc_demo_pupils_v1', JSON.stringify(buildSeedPupils()));
  // The cover tab's whole view is scoped by this ledger: /cover-lessons returns
  // only lessons assigned to the tab's own email. Seeded so /demo/cover opens
  // on a real lesson instead of an empty portal.
  localStorage.setItem(
    'cmc_demo_cover_assignments_v1',
    JSON.stringify(seedCoverLessons.map(l => ({ lessonId: l.id, coverEmail: DEMO_EMAILS.cover(1) }))),
  );

  localStorage.setItem(
    'cmc_tutorial_state',
    JSON.stringify({ completed: false, currentStep: 0, stepsViewed: [], skipped: false }),
  );
}

export function enterDemoSession(role: 'school' | 'teacher' | 'cover') {
  backupRealState();
  // Seed once per demo session, and again whenever the seeded CONTENT changes.
  //
  // The version check is the part that was missing. backupRealState clears
  // cmc_app_state on the FIRST entry, so a cold start always re-seeded and
  // hid the problem; a session already in progress kept its original data for
  // ever, because the backup exists and nothing clears the key. Anyone with a
  // demo tab open across a content change saw none of it.
  const staleSeed = localStorage.getItem(SEED_VERSION_KEY) !== DEMO_SEED_VERSION;
  if (!localStorage.getItem('cmc_app_state')) {
    seedSharedDemoData();
  } else if (!localStorage.getItem('cmc_onboarding_state')) {
    // Edge case: app state exists but onboarding doesn't — seed onboarding
    // alone so the school tab's wizard can mount.
    seedSharedDemoData();
  } else if (staleSeed) {
    seedSharedDemoData();
  }
  activateDemo(role);
}

/**
 * Restore the fixtures the Classes tour consumes as it runs.
 *
 * The teacher tour applies a suggestion and rolls the cohort over — both real
 * actions on real data, which is the point. The consequence is that a second
 * run finds no pending suggestion and a class whose live notes are now
 * archived, so half the steps degrade to explainers. Replaying the tour resets
 * the two classes it touches and re-arms the suggestion.
 *
 * Demo only — there is nothing to reset in a real school, and nothing here
 * should ever run against real data.
 */
/**
 * Deliver the retention-review email.
 *
 * Called when the retention nudge appears — NOT at session seed. Seeded from the
 * start it sat in the mailbox from the school wizard onwards, so an admin who
 * opened the inbox during setup found a message about an annual review of
 * archived observations before they had seen a class, let alone an archive. It
 * has to arrive at the moment the nudge points at it.
 *
 * Idempotent: pushInboxEmail appends, and the nudge can fire again on a replayed
 * Classes tour.
 *
 * Copy mirrors the real annual-review template
 * (functions/src/functions/retentionReviewTimer.ts) including its anti-phishing
 * line, and the CTA is the same ?openReview=1 link production sends — which
 * lands straight on the review screen, bypassing Settings as the real one does.
 *
 * The count is DERIVED from what is actually archived rather than written in. A
 * mailbox quoting a figure the screen contradicts undermines the very thing the
 * message is trying to reassure the reader about.
 */
/**
 * Put the cover teacher's assignment in their mailbox.
 *
 * Called when they choose "Take me to my lesson", NOT on mount. The order is
 * the point: a cover teacher does not open an app and find a lesson waiting,
 * they get an email and the link in it is how they get in. So the scene is set
 * first, then the email actually arrives, the bell announces it, and the
 * mailbox is the route to the lesson rather than a detail mentioned in passing.
 *
 * The CTA carries a real lessonId, picked at random from whichever cover
 * lessons were seeded, so the link lands on a lesson instead of the dashboard.
 * Read from stored state rather than hardcoded, so it cannot drift from the
 * seed.
 *
 * Idempotent by kind, so re-entering the demo does not stack duplicates.
 */
export function deliverCoverAssignmentEmail() {
  try {
    if (!isDemoMode()) return;
    const raw = localStorage.getItem('cmc_demo_inbox_v1');
    const all = raw ? (JSON.parse(raw) as { kind?: string }[]) : [];
    if (all.some(e => e?.kind === 'cover_assignment')) return;

    let stored: AppState | null = null;
    try {
      const st = localStorage.getItem('cmc_app_state');
      stored = st ? (JSON.parse(st) as AppState) : null;
    } catch { /* fall through to the generic email below */ }
    const coverLessons = (stored?.lessons ?? []).filter(l => l.id.startsWith('demo-lesson-cover-'));
    const pick = coverLessons.length
      ? coverLessons[Math.floor(Math.random() * coverLessons.length)]
      : null;

    const where = pick
      ? [
          `${pick.yearGroup} ${pick.subject}`,
          `Period ${pick.periodNumber}, ${pick.periodTime}, Room ${pick.classroom}`,
          `Covering for ${pick.coveringFor}`,
        ]
      : ['Details are in the portal.'];

    pushInboxEmail({
      kind: 'cover_assignment',
      toEmail: DEMO_EMAILS.cover(1),
      toName: 'Demo Cover',
      subject: pick
        ? `Cover confirmed: ${pick.yearGroup} ${pick.subject}, Period ${pick.periodNumber}`
        : 'Cover confirmed for today',
      body: [
        'Hello,',
        '',
        'You have been booked to cover a lesson at Bromley Demo Academy.',
        '',
        ...where,
        '',
        'The lesson plan, and everything the class teacher wanted you to know about this group, is waiting for you. Use the link below to open it. You do not need an account and there is nothing to set up.',
        '',
        'Stay safe: CoverMyClass will never ask for your password by email.',
      ].join('\n'),
      cta: {
        label: pick ? `Open my ${pick.subject} lesson` : 'Open my lesson',
        url: pick ? `/demo/cover?lessonId=${encodeURIComponent(pick.id)}` : '/demo/cover',
      },
    });
  } catch {
    // ignore
  }
}

export function deliverRetentionReviewEmail() {
  try {
    if (!isDemoMode()) return;
    const raw = localStorage.getItem('cmc_demo_inbox_v1');
    const all = raw ? (JSON.parse(raw) as { kind?: string }[]) : [];
    if (all.some(e => e?.kind === 'retention_review')) return;
    const stored = localStorage.getItem('cmc_demo_classes_v1');
    const classes = stored ? (JSON.parse(stored) as ClassEntity[]) : buildSeedClasses();
    const archivedTotal = classes.reduce((n, c) => n + countArchivedEntries(c.continuityPillars), 0);
    pushInboxEmail({
      kind: 'retention_review',
      toEmail: DEMO_EMAILS.schoolAdmin,
      subject: `Retention review: ${archivedTotal} archived observations due for review`,
      body: [
        'Your annual retention review has started.',
        '',
        `${archivedTotal} archived observations across the school are due for review.`,
        '',
        'Nothing is deleted without a grace period. You have time to look through them and restore anything worth keeping first.',
        '',
        'Stay safe: CoverMyClass will never ask for your password by email.',
      ].join('\n'),
      cta: { label: 'Review archived observations', url: '/demo/school?openReview=1' },
    });
  } catch { /* quota / private browsing */ }
}

export function resetClassesTutorialFixture() {
  try {
    if (!isDemoMode()) return;
    const seeded = buildSeedClasses();
    const raw = localStorage.getItem('cmc_demo_classes_v1');
    const current = raw ? (JSON.parse(raw) as ClassEntity[]) : [];
    const restore = new Set(['demo-class-1', 'demo-class-3']);
    const next = current.map((c) => {
      if (!restore.has(c.id)) return c;
      return seeded.find((sc) => sc.id === c.id) ?? c;
    });
    // A class the tour needs but the user deleted comes back too.
    for (const sc of seeded) {
      if (restore.has(sc.id) && !next.some((c) => c.id === sc.id)) next.push(sc);
    }
    localStorage.setItem('cmc_demo_classes_v1', JSON.stringify(next));
    localStorage.setItem('cmc_demo_pillar_suggestions', JSON.stringify(buildSeedPillarSuggestions()));
  } catch { /* quota / private browsing. The tour still runs, just degraded */ }
}

/**
 * Put the archived observations back so the retention tour has something to be
 * about.
 *
 * The retention review's whole job is permanent deletion, and doing it is the
 * point of the walkthrough. That leaves the tour with nothing: replaying it
 * afterwards walked an admin through an empty review, describing classes and
 * notes that no longer existed.
 *
 * Broader than resetClassesTutorialFixture, which restores only the two classes
 * that tour needs. Deletion here sweeps every class in the school, so every
 * seeded class is restored — archived entries, retrieval requests and all.
 */
export function resetRetentionTutorialFixture() {
  try {
    if (!isDemoMode()) return;
    const seeded = buildSeedClasses();
    const raw = localStorage.getItem('cmc_demo_classes_v1');
    const current = raw ? (JSON.parse(raw) as ClassEntity[]) : [];
    // Seeded classes go back to their seeded state; anything the user added
    // themselves is left exactly as it is.
    const next = current.map((c) => seeded.find((sc) => sc.id === c.id) ?? c);
    for (const sc of seeded) {
      if (!next.some((c) => c.id === sc.id)) next.push(sc);
    }
    localStorage.setItem('cmc_demo_classes_v1', JSON.stringify(next));
    localStorage.setItem('cmc_demo_pillar_suggestions', JSON.stringify(buildSeedPillarSuggestions()));
  } catch { /* quota / private browsing. The tour still runs, just degraded */ }
}

export function exitDemoSession() {
  clearAllDemoLikeState();
  try {
    sessionStorage.removeItem(ROLE_KEY);
  } catch {
    // ignore
  }
  const backup = readBackup();
  if (backup) {
    for (const [k, v] of Object.entries(backup.ls)) localStorage.setItem(k, v);
    for (const [k, v] of Object.entries(backup.ss)) sessionStorage.setItem(k, v);
    clearBackup();
  }
  deactivateDemo();
}

export function autoRestoreIfStale() {
  if (typeof window === 'undefined') return;
  if (window.location.pathname.startsWith('/demo')) return;
  // Only auto-restore if demo mode flag was somehow left on but the user
  // is on a production route — otherwise we'd clobber state that's
  // legitimately demo (e.g. someone opening /school/login while testing).
  if (!localStorage.getItem('cmc_demo_mode')) return;
  if (readBackup()) exitDemoSession();
}

// Synthetic Supabase session — consumed by the supabase.ts wrapper Proxy.
// Role is per-tab so the same supabase shim returns different identities
// in the school tab vs the teacher tab.
export function getDemoSupabaseSession() {
  const role = getDemoRole() ?? 'school';
  const userId =
    role === 'school' ? 'demo-school-id' : role === 'teacher' ? 'demo-staff-1' : 'demo-cover-1';
  const email =
    role === 'school'
      ? DEMO_EMAILS.schoolAdmin
      : role === 'teacher'
        ? DEMO_EMAILS.teacher(1)
        : DEMO_EMAILS.cover(1);
  return {
    access_token: `demo-${role}-token`,
    refresh_token: `demo-${role}-refresh`,
    expires_at: Math.floor(Date.now() / 1000) + 3600,
    expires_in: 3600,
    token_type: 'bearer',
    user: {
      id: userId,
      aud: 'authenticated',
      role: 'authenticated',
      email,
      app_metadata: {},
      user_metadata: {
        schoolId: 'demo-school-id',
        role,
      },
      created_at: new Date().toISOString(),
    },
  };
}
